/**
 * Shared hook for submission-scoped filtering on Review and Clear pages.
 *
 * Reads the global submission focus AND docSubmissionMap from
 * SubmissionFocusContext (A2F-459: moved from DashboardCacheContext so
 * filtering works on any page, not just after visiting the Dashboard).
 *
 * A2F-443 Phase 2, refactored for global focus in A2F-454/459.
 * A2F-566: classifyTier prefers server-attached `finding.tier` over local
 * derivation when present. Local fallback stays until A2F-568.
 */

import { useMemo, useCallback } from 'react';
import { useSubmissionFocus } from './SubmissionFocusContext';
import { getSubmissionDocIds, classifyFindingTier } from './findingClassification';
import type { FindingTier } from './findingClassification';
import type { Submission } from '@/types/api';

export interface UseSubmissionFilterResult {
  /** Currently focused submission ID (null = all). */
  submissionId: string | null;
  /** All submissions for the active project. */
  submissions: Submission[];
  /** True when a specific submission is selected (not "All"). */
  isFiltering: boolean;
  /** Set of document IDs belonging to the currently focused submission (null when not filtering). */
  currentDocIds: Set<string> | null;
  /** Classify a finding into a three-tier provenance: new / pre-existing / inherited. */
  classifyTier: (
    finding: { documents?: string[]; tier?: FindingTier | null },
    documentMap: Record<string, string>,
  ) => FindingTier;
}

export function useSubmissionFilter(): UseSubmissionFilterResult {
  const { submissionFocusId, submissions, docSubmissionMap } = useSubmissionFocus();

  const currentDocIds = useMemo(
    () => submissionFocusId && docSubmissionMap
      ? getSubmissionDocIds(docSubmissionMap, submissionFocusId)
      : null,
    [submissionFocusId, docSubmissionMap],
  );

  const classifyTier = useCallback(
    (
      finding: { documents?: string[]; tier?: FindingTier | null },
      documentMap: Record<string, string>,
    ): FindingTier => {
      // Fast path: server has already classified and attached the tier.
      if (finding.tier) return finding.tier;
      if (!currentDocIds) return 'new';
      return classifyFindingTier(finding, documentMap, currentDocIds);
    },
    [currentDocIds],
  );

  return {
    submissionId: submissionFocusId,
    submissions,
    isFiltering: submissionFocusId !== null && docSubmissionMap !== null,
    currentDocIds,
    classifyTier,
  };
}
